import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

export interface KeyboardShortcutsOverlayProps {
  /** Whether the overlay is open. */
  isOpen: boolean;
  /** Called when the user dismisses the overlay (Escape, backdrop click, or
   *  the close button). */
  onClose: () => void;
}

/**
 * Phase 3 Session 3 — Keyboard shortcuts help overlay (`?` key).
 *
 * Standalone modal mirroring <SendDealLinkModal>'s shell pattern
 * (overlay + centered panel + own Escape useEffect + click-outside via
 * overlay onMouseDown). NOT composed on <ConfirmModal> — binary
 * confirm/cancel footer is a mis-fit for a read-only shortcuts list
 * (research/phase-3.md § 4 Block H + § 11 R-4).
 *
 * Content: all Phase 2 + Phase 3 keyboard shortcuts grouped by surface
 * (Navigation / Selection / Find / Edit / Copy / Zoom / Fullscreen). Static —
 * no live data binding, no internal state beyond the open prop.
 *
 * Closes on:
 *   - Escape (own document-level keydown listener).
 *   - Click on backdrop (overlay onMouseDown).
 *   - Click on the X close button in the header.
 *
 * ARIA: role="dialog" + aria-modal="true" + aria-labelledby pointing at the
 * header title (mirrors SendDealLinkModal).
 */
export function KeyboardShortcutsOverlay({
  isOpen,
  onClose,
}: KeyboardShortcutsOverlayProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape closes the modal. Own document-level listener so it does not
  // route through ModelLayout's handleKeyDown.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // F-I4 (post-ship remediation, 2026-05-25): WAI-ARIA `aria-modal="true"`
  // promises focus containment. The close button is the only focusable
  // element inside the panel — without a trap, Tab escapes to the underlying
  // Builder page (tab bar, navigator). Focus the close button on open and
  // bounce Tab/Shift+Tab back to it so focus stays inside.
  useEffect(() => {
    if (!isOpen) return undefined;
    closeBtnRef.current?.focus();
    const trap = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      // Only one focusable element — bounce regardless of direction.
      if (panelRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        closeBtnRef.current?.focus();
      } else {
        // Focus escaped (e.g. backdrop click took focus off-screen) — pull back.
        e.preventDefault();
        closeBtnRef.current?.focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — click-to-close */}
      <div
        data-testid="keyboard-shortcuts-overlay-backdrop"
        className="fixed inset-0 bg-void/70 z-50"
        onMouseDown={onClose}
      />
      {/* Panel — stops propagation so inside-clicks don't close */}
      <div
        ref={panelRef}
        data-testid="keyboard-shortcuts-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyboard-shortcuts-overlay-title"
        aria-describedby="keyboard-shortcuts-overlay-body"
        className={clsx(
          'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
          'w-full max-w-2xl max-h-[80vh] overflow-y-auto',
          'bg-panel-bg border border-panel-border rounded-[3px]',
          'shadow-[0_8px_24px_rgba(0,0,0,0.4)]'
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-panel-border">
          <h2
            id="keyboard-shortcuts-overlay-title"
            className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase"
          >
            Keyboard Shortcuts
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
            className="text-text-muted hover:text-text-bright transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent focus-visible:outline-offset-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Shortcut groups */}
        <div id="keyboard-shortcuts-overlay-body" className="px-4 py-3">
          {SHORTCUT_GROUPS.map((group) => (
            <section key={group.label} className="mb-4 last:mb-0">
              <h3 className="text-[10px] font-mono uppercase tracking-[2px] text-accent mb-2">
                {group.label}
              </h3>
              <table className="w-full text-sm font-body">
                <tbody>
                  {group.shortcuts.map(({ keys, description }) => (
                    <tr
                      key={keys.join('+') + description}
                      className="align-top"
                    >
                      <td className="py-1 pr-4 whitespace-nowrap">
                        {keys.map((k, i) => (
                          <span key={`${k}-${i}`}>
                            <kbd className="px-1.5 py-0.5 bg-panel-border rounded text-[10px] font-mono text-text-bright">
                              {k}
                            </kbd>
                            {i < keys.length - 1 && (
                              <span className="text-text-muted mx-1">+</span>
                            )}
                          </span>
                        ))}
                      </td>
                      <td className="py-1 text-text-muted">{description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Shortcut groups — static. Mirrors every keyboard shortcut shipped in
// Phases 2 + 3. Update this list any time a new shortcut is added in a
// later feature or backlog item.
// ============================================================================
interface ShortcutEntry {
  keys: string[];
  description: string;
}
interface ShortcutGroup {
  label: string;
  shortcuts: ShortcutEntry[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    label: 'Navigation',
    shortcuts: [
      { keys: ['↑', '↓', '←', '→'], description: 'Move selection one cell' },
      { keys: ['Tab'], description: 'Next column (wraps to next row)' },
      { keys: ['Shift', 'Tab'], description: 'Previous column' },
      { keys: ['Enter'], description: 'Next row, same column' },
      { keys: ['Shift', 'Enter'], description: 'Previous row, same column' },
      { keys: ['Page Down'], description: 'Down by viewport height' },
      { keys: ['Page Up'], description: 'Up by viewport height' },
      { keys: ['Home'], description: 'First column of current row' },
      { keys: ['End'], description: 'Last column of current row' },
      { keys: ['Ctrl/⌘', 'Home'], description: 'First cell of sheet' },
      { keys: ['Ctrl/⌘', 'End'], description: 'Last cell of sheet' },
      {
        keys: ['Ctrl/⌘', '↑/↓/←/→'],
        description: 'Jump to next non-empty cell (Excel data-edge)',
      },
      { keys: ['Esc'], description: 'Clear selection' },
    ],
  },
  {
    label: 'Selection',
    shortcuts: [
      { keys: ['Click'], description: 'Select cell' },
      { keys: ['Right-click'], description: 'Open context menu (6 actions)' },
    ],
  },
  {
    label: 'Find',
    shortcuts: [
      { keys: ['Ctrl/⌘', 'F'], description: 'Focus tree search' },
      { keys: ['Ctrl/⌘', 'G'], description: 'Find next match in grid' },
      { keys: ['Ctrl/⌘', 'Shift', 'G'], description: 'Find previous match' },
      { keys: ['?'], description: 'Show this help overlay' },
    ],
  },
  {
    label: 'Edit',
    shortcuts: [
      {
        keys: ['Enter', 'Space'],
        description: 'Open cell editor (input cells)',
      },
    ],
  },
  {
    label: 'Copy',
    shortcuts: [
      { keys: ['Ctrl/⌘', 'C'], description: 'Copy formatted cell value' },
      { keys: ['Ctrl/⌘', 'Shift', 'C'], description: 'Copy raw formula' },
    ],
  },
  {
    label: 'Zoom',
    shortcuts: [
      { keys: ['Ctrl/⌘', 'Shift', '='], description: 'Zoom in' },
      { keys: ['Ctrl/⌘', 'Shift', '-'], description: 'Zoom out' },
      { keys: ['Ctrl/⌘', 'Shift', '0'], description: 'Reset to 100%' },
    ],
  },
  {
    label: 'Fullscreen',
    shortcuts: [
      { keys: ['F'], description: 'Toggle fullscreen' },
      { keys: ['Esc'], description: 'Exit fullscreen' },
    ],
  },
];
